import {
  ISpaceType,
  IProjectSpaceType,
} from 'spw-redux/modules/space_types/types';
import { REQUEST_STATUS } from 'spw-constants';
import {
  IDepartment,
  IBuilding,
  IFloor,
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from '../projects/types';
import { PlainProgramScenario } from '../../../models/Scenario';
import { PlainWorksheet } from '../../../models/Worksheet';

export interface ICalculatorState {
  currentScenario: PlainProgramScenario;
  currentCalculatorType: null | CalculatorType;
  showScenarioDialog: boolean;
  showWorksheetUpdateDialog: boolean;
  showWorksheetNotesDialog: boolean;
  showTotalProgramDialog: boolean;
  areScenariosBeingRetrieved: boolean;
  scenariosRequestStatus: REQUEST_STATUS;
  totalProgramsRequestStatus: REQUEST_STATUS;
  worksheetRequestStatus: REQUEST_STATUS;
  worksheetsRequestStatus: REQUEST_STATUS;
  scenarios: PlainProgramScenario[];
  isEditingSavedScenario: boolean;
  scenarioFloorId?: null | number;
  worksheet: PlainWorksheet;
  isLoading: boolean;
  isDeleteConfirmationOpen: boolean;
  temporaryScenario: PlainProgramScenario;
  duplicated: boolean;
  step?: null | string;
  isUpdate: boolean;
  totalPrograms: PlainProgramScenario[];
  worksheets: PlainWorksheet[];
  tpsWorksheetsEnhanced: PlainWorksheet[];
  childWorksheetSpacesRequestStatus: REQUEST_STATUS;
  childWorksheetSpaces: IChildWorksheetSpaceType[];
  isCalculatorDirty: boolean;
  isUnsavedDialogOpen: boolean;
  isSavingScenario: boolean;
  scenarioListType?: string;
}

export interface GrowthEntry {
  year: number;
  value: number;
}

export interface Growth {
  department?: IDepartment;
  headcountSeatCountGrowthRate?: number;
  headcountSeatCountBase: number;
  growthEntries: GrowthEntry[];
  aggregation?: boolean;
}

export interface ManualGrowthEntryUpdate {
  headcount: number;
  headcountIndex: number;
  otherEntriesAreClean: boolean;
}

export enum WorkSeatsRoundingSetting {
  RoundUp = 'round_up',
  RoundDown = 'round_down',
}

export interface Scenario {
  calculatorType: CalculatorType;
  targetUsf: number;
  vacancyFactor: string | number;
  headcountUtilization: string | number;
  deskSharingRatio: string | number;
  startYear: number;
  endYear: number;
  buildingId?: number;
  building?: IBuilding;
  projectId: number;
  floorId?: number;
  floor?: IFloor;
  headcountSeatCountBase?: number;
  headcountSeatCountGrowthRate?: number;
  workSeatsRoundingSetting?: WorkSeatsRoundingSetting | null;
  name: string;
  note: string;
  id: number;
  targetYear: number;
  subtotalBuildingMultiplier: number;
  subtotalProgramMultiplier: number;
  department?: IDepartment;
  departmentId?: number;
  growthYears?: number;
  growths?: number[];
  index?: number;
  base?: number;
  isDetailed: boolean;
  isShared: boolean;
  usf?: number;
  rsfLoadFactor: number;
  storedWorkSeats: number | null;
  areStoredWorkSeatsOutdated?: boolean;
  worksheet?: IWorksheet;
}

export interface ApiScenario extends Omit<Scenario, 'growths'> {
  hcscBase: number;
  hcscGrowthRate: number;
  departmentId?: number;
}

export type UsfType = 'sum_linked_scenarios' | 'total_program_only';

export type CalculatorType =
  | 'building-headcount-seatcount'
  | 'building-headcount-seatcount-departments'
  | 'floor-headcount-seatcount'
  | 'building-usf'
  | 'floor-usf'
  | 'macro-programming'
  | 'total-program';

export const BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS =
  'building-headcount-seatcount-departments';

export const BUILDING_USF = 'building-usf';
export const BUILDING_HEADCOUNT_SEATCOUNT = 'building-headcount-seatcount';
export const FLOOR_HEADCOUNT_SEATCOUNT = 'floor-headcount-seatcount';
export const FLOOR_USF = 'floor-usf';
export const ALL_DEPARTMENTS_OPTION = 'all-departments';

export const YEARS_AHEAD = 10;

export interface IWorksheetRow {
  id?: number;
  quantity: number;
  seatCap: number;
  sizeX: number;
  sizeY: number;
  usf: number;
  ratio: number;
  projectSpaceType?: Partial<IProjectSpaceType>;
  spaceType?:
    | Omit<ISpaceType, 'spaceTypeCategory'>
    | Omit<IChildWorksheetSpaceType, 'spaceTypeCategory'>;
  category?: IProjectSpaceTypeCategory;
  subcategory?: IProjectSpaceTypeSubcategory | null;
  removed?: boolean;
  child?: boolean;
  spaceId?: number;
  department?: IDepartment;
  totalUsf?: number;
  spaceTypeId?: number;
  worksheetId?: number;
  scenarioId?: number;
}

export interface ICategoryNote {
  id?: number;
  note: string;
  category: string;
}

export interface IWorksheetRowAPIObject extends IWorksheetRow {
  spaceId: number;
  projectSpaceTypeId: number;
}

export interface WorksheetCategory {
  worksheetId?: number;
  categoryId: number;
  visible: boolean;
}

export interface IWorksheet {
  id?: number;
  rows: IWorksheetRow[];
  notes?: ICategoryNote[];
  categories?: WorksheetCategory[];
  scenarioId: number;
  spaceAllocation?: number;
}

export interface IWorksheetAPIObject extends IWorksheet {
  worksheetRows: IWorksheetRowAPIObject[];
}

export const isWorksheetAPIObject = (ws: any): ws is IWorksheetAPIObject =>
  !!ws?.worksheetRows;

export type IWorksheetRowValue = 'target_ratio' | 'quantity';

export const isProgramScenario = (sc: any): sc is PlainProgramScenario =>
  sc?.headcountUtilizationPercentage !== undefined;

export interface ITotalProgramScenario {
  id: number;
  scenarioId: number;
  totalProgramId: number;
  scenario?: Scenario;
}

export interface ITotalProgram {
  id: number;
  name: string;
  index: number;
  calculatorType: 'total-program';
  totalProgramScenario: ITotalProgramScenario[];
  subtotalProgramMultiplier: number;
  planningFactor: number;
  usf: number;
  usfType: UsfType;
  notes?: ICategoryNote[];
  projectId: number;
}

export interface IChildWorksheetSpaceType {
  id: number;
  name: string;
  clientFacingName?: string;
  spaceTypeId: number;
  projectId: number;
  spaceType: string;
  parent: ISpaceType | null;
  icon: string;
  unitsHigh?: number;
  rank: string;
}
